import { useState } from 'react';

interface SearchFiltersProps {
  onSearch: (filters: any) => void;
}

export default function SearchFilters({ onSearch }: SearchFiltersProps) {
  const [searchType, setSearchType] = useState('orderNo');
  const [searchValue, setSearchValue] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [orderType, setOrderType] = useState('');
  const [paymentChannels, setPaymentChannels] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [orderTimeRange, setOrderTimeRange] = useState({ start: '', end: '' });
  const [paymentTimeRange, setPaymentTimeRange] = useState({ start: '', end: '' });

  const handleSearch = () => {
    onSearch({
      searchType,
      searchValue,
      paymentMethods,
      orderType,
      paymentChannels,
      statuses,
      orderTimeRange,
      paymentTimeRange
    });
  };

  const handleReset = () => {
    setSearchType('orderNo');
    setSearchValue('');
    setPaymentMethods([]);
    setOrderType('');
    setPaymentChannels([]);
    setStatuses([]);
    setOrderTimeRange({ start: '', end: '' });
    setPaymentTimeRange({ start: '', end: '' });
    onSearch({});
  };

  const toggleArrayValue = (arr: string[], value: string, setter: (arr: string[]) => void) => {
    if (arr.includes(value)) {
      setter(arr.filter(v => v !== value));
    } else {
      setter([...arr, value]);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 mb-6">
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">搜索类型</label>
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="orderNo">订单号</option>
            <option value="paymentNo">支付单号</option>
            <option value="productName">商品名称</option>
            <option value="userName">下单用户名</option>
            <option value="userPhone">手机号码</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">搜索内容</label>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="请输入搜索内容"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">下单类型</label>
          <select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">全部</option>
            <option value="新购">新购</option>
            <option value="续费">续费</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">付费方式</label>
          <div className="flex flex-wrap gap-2">
            {['按时计费', '包年包月', '购买时长'].map(method => (
              <label key={method} className="flex items-center text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentMethods.includes(method)}
                  onChange={() => toggleArrayValue(paymentMethods, method, setPaymentMethods)}
                  className="mr-2"
                />
                {method}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">支付方式</label>
          <div className="flex flex-wrap gap-2">
            {['支付宝', '微信', '余额'].map(channel => (
              <label key={channel} className="flex items-center text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentChannels.includes(channel)}
                  onChange={() => toggleArrayValue(paymentChannels, channel, setPaymentChannels)}
                  className="mr-2"
                />
                {channel}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">订单状态</label>
          <div className="flex flex-wrap gap-2">
            {['待支付', '已支付', '已取消', '已退款', '部分退款', '已完成'].map(status => (
              <label key={status} className="flex items-center text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={statuses.includes(status)}
                  onChange={() => toggleArrayValue(statuses, status, setStatuses)}
                  className="mr-2"
                />
                {status}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">下单时间</label>
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={orderTimeRange.start}
              onChange={(e) => setOrderTimeRange({ ...orderTimeRange, start: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <span className="text-gray-500">至</span>
            <input
              type="datetime-local"
              value={orderTimeRange.end}
              onChange={(e) => setOrderTimeRange({ ...orderTimeRange, end: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">支付时间</label>
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={paymentTimeRange.start}
              onChange={(e) => setPaymentTimeRange({ ...paymentTimeRange, start: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <span className="text-gray-500">至</span>
            <input
              type="datetime-local"
              value={paymentTimeRange.end}
              onChange={(e) => setPaymentTimeRange({ ...paymentTimeRange, end: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={handleReset}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 whitespace-nowrap"
        >
          重置
        </button>
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 whitespace-nowrap"
        >
          搜索
        </button>
      </div>
    </div>
  );
}
